import type { ReadonlyURLSearchParams } from 'next/navigation';

import { SHOP_BY_PREFIX } from '@/components/Analytics/constants';
import {
    GRS_SEARCH_PROVIDER_QUERY_PARAM_VALUE,
    SEARCH_SERVICE_PROVIDERS,
} from '@/constants/grs';
import {
    BOPIM_PICK_UP_TEXT_KEY,
    GRS_REFINE_FILTER_KEY_VALUE_DELIMITER,
    ITEM_LOCATION_BOPIM_KEY,
    LW_REFINE_FILTER_KEY_VALUE_DELIMITER,
    REFINEMENT_KEY_DELIVERY,
    REFINE_QUERY_DELIMITER,
} from '@/constants/index';
import { getKeyValue } from '@/services/content/data';
import { SearchResultGRS } from '@/services/search/services/GRS.types';
import {
    getFacetKeyFromQueryParamKey,
    getGRSFacetsFromConfig,
} from '@/services/search/services/Grs';
import { SearchResult, SearchServiceProps } from '@/services/search/types';
import { GRS_USE_WAREHOUSE_INVENTORY_BUCKET_VALUE } from '@/src/components/SearchResultsFacets/grs.constants';
import { GRSFacetConfig, GRSFacetConfigProperties } from '@/types/contentStack';
// import { checkGRSRefinementApplied } from '@/src/components/SearchResultsFacets/grs.lib';
import type { Facet, SearchResultLW } from '@/types/searchQuery';
import { splitByCommaOutsideParentheses } from '@/utils/searchFacets';
import {
    type ComposableWindowArray,
    type ContentStackEntryDataProps,
    type LocaleProps,
    getComposableTextByDevFieldKey,
    getFormattedPrice,
} from '@nxt-costco-com/forge-digital-components';

import {
    BUY_IN_WAREHOUSE_FACET_VALUE,
    BUY_IN_WAREHOUSE_TEXT_KEY,
    CURRENT_PAGE,
    DEFAULT_REFINEMENT_FILTER_PAIRS,
    DELIVERY_FACET_VALUE,
    DELIVERY_TEXT_KEY,
    GRS_ATTRIBUTE_FACET_KEY_PREFIX,
    GRS_BOPIM_FACET_KEY,
    GRS_BOPIW_FACET_KEY,
    GRS_BRANDS_FACET_KEY,
    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
    GRS_CATEGORY_FACET_KEY,
    GRS_DELIVERY_FACET_KEY,
    GRS_RATING_FACET_KEY,
    GRS_REFINE_QUERY_PARAM,
    GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
    GRS_TWO_DAY_FACET_KEY,
    IN_WAREHOUSE_BUCKET_VALUE,
    LW_ATTRIBUTE_FACET_KEY_SUFFIX,
    LW_BOPIM_FACET_KEY,
    LW_BOPIW_FACET_KEY,
    LW_BRANDS_FACET_KEY,
    LW_BUY_IN_WAREHOUSE_FACET_KEY,
    LW_CATEGORY_FACET_KEY,
    LW_DELIVERY_FACET_KEY,
    LW_OUT_OF_STOCK_QUERY_PARAM_VALUE,
    LW_PRICE_FACET_KEY,
    LW_RATING_FACET_KEY,
    LW_REFINE_QUERY_PARAM,
    LW_SHOW_OUT_OF_STOCK_FACET_KEY,
    LW_TWO_DAY_FACET_KEY,
    NEGATION_REFINEMENT_FILTER_PAIRS,
    OUT_OF_STOCK_BUCKET_VALUE,
    OUT_OF_STOCK_TEXT_KEY,
    PICK_UP_TEXT_KEY,
    PRICE_LABEL_KEY,
    RATING_LABEL_VALUE_KEY,
    RATING_TAG_LABEL_KEY,
    STANDARD_REFINEMENT_FILTER_KEYS,
    TWO_DAY_DELIVERY_TEXT_KEY,
    TWO_DAY_FACET_VALUE,
    booleanValueFacets,
    rangeValueFacets,
} from './constants';
import { isGRSPriceFacetKey, normalizeGRSFacets } from './grs.lib';
import type { FacetBucketNormalized, FacetNormalized } from './types';

/** Determine whether a raw refinement pair (from the URL) should be treated
 * as a non-default refinement. Returns true when the pair is a non-default
 * refinement that should be considered for labels/counts.
 *
 * This predicate is consumed by `getSingleNonDefaultRefinementLabel` and
 * `getSingleNonDefaultRefinementsCount` to decide which filters should be
 * surfaced to users. Those labels are used in places such as:
 *  - the browser tab title prefix (via `updatePageTitle`), e.g.:
 *      ACME keyword | Costco
 *  - the visible title/header above search results when a single
 *    non-default refinement is applied.
 *
 * Important behaviour:
 *  - GRS dynamic price facets (keys like `inventory(1668, price)`) are
 *    intentionally treated as STANDARD refinements and therefore excluded
 *    (this function returns `false`) to avoid prefixing the page title
 *    with raw price ranges.
 *
 * Examples (raw refinement pair strings):
 *  - GRS dynamic price facet (excluded → returns false):
 *      inventory(1668, price)=(0,25)
 *  - GRS non-price facet (may be non-default → returns true):
 *      brand=(ACME)
 *
 * @param refinementPair raw "key<delimiter>value" string from the URL
 * @param refineFilterKeyValueDelimiter delimiter used for key/value (e.g. '=' or '-')
 * @param searchService provider (`SEARCH_SERVICE_PROVIDERS.GRS` or LW)
 * @param grsFacetsMap optional GRS facet config map for key mapping
 */
function isNonDefaultRefinementPair(
    refinementPair: string | undefined,
    refineFilterKeyValueDelimiter:
        | typeof LW_REFINE_FILTER_KEY_VALUE_DELIMITER
        | typeof GRS_REFINE_FILTER_KEY_VALUE_DELIMITER,
    searchService: SearchServiceProps,
    grsFacetsMap: GRSFacetConfig,
): boolean {
    const [existingRefinementQueryParamKey] = splitRefinementPairs(
        refinementPair,
        refineFilterKeyValueDelimiter,
    );

    if (!existingRefinementQueryParamKey) return false;

    const mappedKey =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? (getFacetKeyFromQueryParamKey(
                  existingRefinementQueryParamKey,
                  grsFacetsMap,
              ) ?? existingRefinementQueryParamKey)
            : existingRefinementQueryParamKey;

    // Treat dynamic GRS price facet keys (e.g. inventory(..., price)) as standard
    if (
        searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
        isGRSPriceFacetKey(mappedKey ?? '')
    ) {
        return false;
    }

    return !STANDARD_REFINEMENT_FILTER_KEYS.includes(mappedKey!);
}

function isEncoded(uri?: string | null) {
    uri = uri || '';

    return uri !== decodeURIComponent(uri);
}

export function fullyDecodeURI(uri?: string | null) {
    if (!uri) return;

    /**
     * Due to an unfortunate complication with Legacy refinements being double encoded and + symbols having complex interactions with space characters,
     * the following logic helps us fix legacy URLs. Currently we identify legacy refinements by them starting with ||, which we check after our first decoding
     * (which is done by toStringing search params which happens at some point before this method is called) to catch the possibility
     * that the | character is encoded.
     **/

    // Need to revisit, not a permanent solve, if condition specifically handles refine=GPS + Cellular kind of refine params,
    // rest of the other cases where LW/legacy can have encoding space > + > %2B, we still want to replace + with space manually while decoding
    if (!uri.includes(' + ')) uri = uri.replace(/\+/g, ' ');

    while (isEncoded(uri)) {
        uri = decodeURIComponent(uri);
    }

    return uri;
}

/** true if the hide out of stock facet filter should be shown and be disabled by default instead of showing "show out of stock" and enabled by default
 * @deprecated It has been agreed to completely move away from "hide out of stock" facet label into "show out of stock items", no need to use this flag anymore
 */
export function isEnableHideOutOfStockFilter(
    config: ContentStackEntryDataProps | undefined,
): boolean {
    if (!config) return false;
    return !!(
        Array.isArray(config.properties) &&
        config.properties[0] &&
        typeof config.properties[0] === 'object' &&
        'enableHideOutOfStockFilter' in config.properties[0] &&
        (
            config.properties[0] as {
                enableHideOutOfStockFilter?: boolean;
            }
        ).enableHideOutOfStockFilter
    );
}

export function getFacetLabel(
    bucketValue: string,
    config: ContentStackEntryDataProps | undefined,
    searchService?: SearchServiceProps,
) {
    if (!config) {
        console.warn('Missing facet label config');
        return null;
    }

    switch (bucketValue?.toLowerCase()) {
        case DELIVERY_FACET_VALUE.toLowerCase():
            return getKeyValue(DELIVERY_TEXT_KEY, config);
        case TWO_DAY_FACET_VALUE.toLowerCase():
            return getKeyValue(TWO_DAY_DELIVERY_TEXT_KEY, config);
        case OUT_OF_STOCK_BUCKET_VALUE:
            return getKeyValue(OUT_OF_STOCK_TEXT_KEY, config);
        case GRS_USE_WAREHOUSE_INVENTORY_BUCKET_VALUE:
            return searchService === GRS_SEARCH_PROVIDER_QUERY_PARAM_VALUE
                ? getKeyValue(PICK_UP_TEXT_KEY, config)
                : null;
        case IN_WAREHOUSE_BUCKET_VALUE:
            return getKeyValue(BUY_IN_WAREHOUSE_TEXT_KEY, config);
        //TODO: There are unused values in this function, clean up later
        case ITEM_LOCATION_BOPIM_KEY:
            return getKeyValue(BOPIM_PICK_UP_TEXT_KEY, config);
        case PICK_UP_TEXT_KEY:
            return getKeyValue(PICK_UP_TEXT_KEY, config);
        default:
            return null;
    }
}

/**
 * Even though Lucidworks / GRS returns a display order, they are not in the order the business
 * wants them, so we need to remap the the top few groups.... :shrug:
 */
export function remapFacetDisplayOrder(facet: FacetNormalized) {
    if (
        facet.facetKey === LW_CATEGORY_FACET_KEY ||
        facet.facetKey === GRS_CATEGORY_FACET_KEY
    ) {
        facet.display_order = 0;
    } else if (
        facet.facetKey === LW_TWO_DAY_FACET_KEY ||
        facet.facetKey === GRS_TWO_DAY_FACET_KEY
    ) {
        facet.display_order = 1;
    } else if (
        facet.facetKey === LW_DELIVERY_FACET_KEY ||
        facet.facetKey === GRS_DELIVERY_FACET_KEY
    ) {
        facet.display_order = 2;
    } else if (
        facet.facetKey === LW_BOPIM_FACET_KEY ||
        facet.facetKey === GRS_BOPIM_FACET_KEY
    ) {
        facet.display_order = 3;
    } else if (
        facet.facetKey === LW_BUY_IN_WAREHOUSE_FACET_KEY ||
        facet.facetKey === GRS_BUY_IN_WAREHOUSE_FACET_KEY
    ) {
        facet.display_order = 4;
    } else if (
        facet.facetKey === LW_BOPIW_FACET_KEY ||
        facet.facetKey === GRS_BOPIW_FACET_KEY
    ) {
        facet.display_order = 5;
    } else if (
        facet.facetKey === LW_SHOW_OUT_OF_STOCK_FACET_KEY ||
        facet.facetKey === GRS_SHOW_OUT_OF_STOCK_FACET_KEY
    ) {
        facet.display_order = 6;
    } else if (
        facet.facetKey === LW_PRICE_FACET_KEY ||
        isGRSPriceFacetKey(facet.facetKey)
    ) {
        facet.display_order = 7;
    } else if (
        facet.facetKey === LW_RATING_FACET_KEY ||
        facet.facetKey === GRS_RATING_FACET_KEY
    ) {
        facet.display_order = 8;
    } else if (
        facet.facetKey === LW_BRANDS_FACET_KEY ||
        facet.facetKey === GRS_BRANDS_FACET_KEY
    ) {
        facet.display_order = 9;
    } else if (!facet.display_order) {
        facet.display_order = 10;
    }

    return facet;
}

export function sortFacets(a: FacetNormalized, b: FacetNormalized) {
    return (a.display_order ?? 0) - (b.display_order ?? 0);
}

export function modifyRangeString(input: string): string {
    // Use a regex to match the pattern "<digits>+to+<digits>"
    const regex = /\[?(\d+)[\+\s]?to[\+\s]?(\d+)\]?/gi;

    // Replace matches with the desired format "[<digits> TO <digits>]"
    return input.replace(regex, (_, start, end) => `[${start} TO ${end}]`);
}

export function isNonDefaultFacetApplied(
    searchParams: URLSearchParams,
    searchService: SearchServiceProps,
) {
    const refinementKey =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_QUERY_PARAM
            : LW_REFINE_QUERY_PARAM;
    const refineQueryParam = fullyDecodeURI(searchParams.get(refinementKey));

    const refinementArray =
        refineQueryParam?.split(REFINE_QUERY_DELIMITER) || [];
    const nonDefaultRefinementArray = refinementArray.filter(
        refinement => refinement !== REFINEMENT_KEY_DELIVERY,
    );

    return nonDefaultRefinementArray.length > 0;
}

export function checkIfFacetValueExists(
    facetList: Facet[],
    refinement: string,
    refineFilterKeyValueDelimiter:
        | typeof LW_REFINE_FILTER_KEY_VALUE_DELIMITER
        | typeof GRS_REFINE_FILTER_KEY_VALUE_DELIMITER,
) {
    const [facetKey, val] = splitRefinementPairs(
        refinement,
        refineFilterKeyValueDelimiter,
    );
    const facet = facetList?.find(group => group.facetKey === facetKey);

    if (!facet) {
        return false;
    }

    if (facet.buckets.find(bucket => bucket.val === val)) {
        return true;
    }

    return false;
}

export const getQueryParamValue = (
    facet: FacetNormalized,
    bucket: FacetBucketNormalized,
    searchService: SearchServiceProps,
    facetConfig?: ContentStackEntryDataProps,
): string => {
    if (!bucket || !facet) {
        return '';
    }

    if (
        searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
        facet.facetKey === LW_SHOW_OUT_OF_STOCK_FACET_KEY
    ) {
        return LW_OUT_OF_STOCK_QUERY_PARAM_VALUE;
    } else if (
        searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
        facet.facetKey === LW_PRICE_FACET_KEY
    ) {
        return `${bucket.low} TO ${bucket.high}`;
    } else if (
        searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
        facet.facetKey === LW_RATING_FACET_KEY
    ) {
        // Derive the numeric rating from the bucket
        const ratingSource: string | number | undefined =
            bucket.low ?? bucket.val;
        const ratingValue =
            typeof ratingSource === 'number'
                ? ratingSource
                : typeof ratingSource === 'string'
                  ? parseInt(ratingSource, 10)
                  : NaN;

        if (!Number.isFinite(ratingValue)) return '';

        if (facetConfig && facetConfig.composable_window) {
            const formattedRatingValue = getComposableTextByDevFieldKey({
                devFieldKey: RATING_LABEL_VALUE_KEY,
                composableWindow:
                    facetConfig.composable_window as ComposableWindowArray,
                templateVariables: {
                    ratingMinimum: ratingValue.toString(),
                },
            });

            // If the template returns a usable value, use it; otherwise, fall back to a simple label
            if (formattedRatingValue) return formattedRatingValue;
        }

        // Fallback when facetConfig/template is unavailable: preserve the previous default format
        return `${ratingValue} & Up`;
    } else if (
        rangeValueFacets.includes(facet.facetKey) ||
        isGRSPriceFacetKey(facet.facetKey)
    ) {
        // rangeValueFacets covers static BC keys; isGRSPriceFacetKey covers dynamic BD keys
        // (e.g. "inventory(1668, price)") that aren't in the static list
        return `(${bucket.low},${bucket.high})`;
    } else if (booleanValueFacets.includes(facet.facetKey)) {
        return 'true';
    } else {
        return bucket.val?.toString() || '';
    }
};

export function checkRefinementApplied({
    facet,
    bucket,
    searchParams,
    searchService,
    facetConfig,
}: {
    facet: FacetNormalized;
    bucket: FacetBucketNormalized;
    searchParams: ReadonlyURLSearchParams;
    searchService: SearchServiceProps;
    facetConfig: ContentStackEntryDataProps;
}): boolean {
    const queryParamKey = facet.queryParamKey ?? facet.facetKey;
    const queryParamValue = getQueryParamValue(
        facet,
        bucket,
        searchService,
        facetConfig,
    );
    const refinementKey =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_QUERY_PARAM
            : LW_REFINE_QUERY_PARAM;
    const refineFilterKeyValueDelimiter =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_FILTER_KEY_VALUE_DELIMITER
            : LW_REFINE_FILTER_KEY_VALUE_DELIMITER;
    const currentFilterPair = `${queryParamKey}${refineFilterKeyValueDelimiter}${queryParamValue}`;

    const isNegatedFacet =
        NEGATION_REFINEMENT_FILTER_PAIRS.includes(currentFilterPair);

    const hasRefineParam = searchParams.has(refinementKey);
    if (!hasRefineParam) {
        const currentRefinementFilterPair = `${facet.facetKey}${refineFilterKeyValueDelimiter}${queryParamValue}`;
        if (
            DEFAULT_REFINEMENT_FILTER_PAIRS.includes(
                currentRefinementFilterPair,
            ) ||
            isNegatedFacet
        ) {
            return true;
        }
        return false;
    }
    const refinementArray =
        fullyDecodeURI(searchParams.get(refinementKey))?.split(
            REFINE_QUERY_DELIMITER,
        ) || [];
    const currentQueryParamContainingFilterPairs = refinementArray.filter(
        filterPair => filterPair.includes(queryParamKey),
    );
    let inUrl = false;
    currentQueryParamContainingFilterPairs.forEach(currentFilterPair => {
        if (!inUrl) {
            const [_, currentFilterValuesString] = splitRefinementPairs(
                currentFilterPair,
                refineFilterKeyValueDelimiter,
            );
            const queryParamExistingValuesCommaSeparated =
                splitByCommaOutsideParentheses(
                    currentFilterValuesString || '',
                )?.map(value => value?.trim()?.toLowerCase() || '') || [];
            if (
                queryParamExistingValuesCommaSeparated?.includes(
                    queryParamValue?.trim()?.toLowerCase() || '',
                )
            ) {
                inUrl = true;
            }
        }
    });

    // Negated toggles are inverted in LW based on QP. E.g: Show out of stock
    if (isNegatedFacet) {
        inUrl = !inUrl;
    }
    return inUrl;
}

/**
 * Updates search refinements by adding or removing filter values from URL search parameters.
 *
 * This function manages the state of search filters by:
 * - Adding new filter values when a facet is enabled
 * - Removing filter values when a facet is disabled
 * - Handling multiple values for the same filter key
 * - Resetting pagination when filters change
 *
 * @param updateSearchParamsProvider - Function to update the search parameters in the URL
 * @param facet - The normalized facet object containing filter metadata
 * @param bucket - The normalized facet bucket containing the specific filter value
 * @param isEnabled - Whether the filter should be applied (true) or removed (false)
 * @param urlParams - Current URL search parameters to be modified
 * @param searchService - The search service provider configuration (GRS or LW)
 * @param facetConfig - Configuration data for the facet from Content Stack
 *
 */
export const updateRefinements = (
    updateSearchParamsProvider: (queryParams: ReadonlyURLSearchParams) => void,
    facet: FacetNormalized,
    bucket: FacetBucketNormalized,
    isEnabled: boolean,
    urlParams: ReadonlyURLSearchParams,
    searchService: SearchServiceProps,
    facetConfig?: ContentStackEntryDataProps,
) => {
    let isFilterEnabled = isEnabled;
    const queryParams = new URLSearchParams(urlParams);
    //remove pagination page counter when applying filters to avoid ending up no search results
    queryParams.delete(CURRENT_PAGE);
    const queryParamKey = facet.queryParamKey ?? facet.facetKey;
    const queryParamValue = getQueryParamValue(
        facet,
        bucket,
        searchService,
        facetConfig,
    );
    const refinementKey =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_QUERY_PARAM
            : LW_REFINE_QUERY_PARAM;
    const refineFilterKeyValueDelimiter =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_FILTER_KEY_VALUE_DELIMITER
            : LW_REFINE_FILTER_KEY_VALUE_DELIMITER;

    const eligibleDefaultRefinementFilterPairs: {
        key: string;
        value: string;
    }[] = [];
    const eligibleNegatedRefinementFilterPairs: {
        key: string;
        value: string;
    }[] = [];
    DEFAULT_REFINEMENT_FILTER_PAIRS.forEach(defaultRefinementFilterPair => {
        const [defaultFilterPairKey, defaultFilterPairValue] =
            splitRefinementPairs(
                defaultRefinementFilterPair,
                refineFilterKeyValueDelimiter,
            );
        if (defaultFilterPairKey && defaultFilterPairValue) {
            eligibleDefaultRefinementFilterPairs.push({
                key: defaultFilterPairKey,
                value: defaultFilterPairValue,
            });
        }
    });

    NEGATION_REFINEMENT_FILTER_PAIRS.forEach(negatedRefinementFilterPair => {
        const [negatedFilterPairKey, negatedFilterPairValue] =
            splitRefinementPairs(
                negatedRefinementFilterPair,
                refineFilterKeyValueDelimiter,
            );
        if (negatedFilterPairKey && negatedFilterPairValue) {
            eligibleNegatedRefinementFilterPairs.push({
                key: negatedFilterPairKey,
                value: negatedFilterPairValue,
            });
        }
    });

    let refinementArray =
        fullyDecodeURI(queryParams.get(refinementKey))?.split(
            REFINE_QUERY_DELIMITER,
        ) || [];

    if (
        eligibleNegatedRefinementFilterPairs.some(
            item =>
                item.key === queryParamKey && item.value === queryParamValue,
        )
    ) {
        isFilterEnabled = !isEnabled;
    }

    if (
        isFilterEnabled &&
        refinementArray.length === 0 &&
        !eligibleDefaultRefinementFilterPairs.some(
            item => item.key === queryParamKey,
        )
    ) {
        eligibleDefaultRefinementFilterPairs.forEach(
            eligibleDefaultFilterPairObj => {
                const defaultFilterKey = eligibleDefaultFilterPairObj.key;
                const defaultFilterValue = eligibleDefaultFilterPairObj.value;

                const commonFilterRefinementArrayIndex =
                    refinementArray.findIndex(refinementFilterPair =>
                        refinementFilterPair.startsWith(
                            `${defaultFilterKey}${refineFilterKeyValueDelimiter}`,
                        ),
                    );

                if (commonFilterRefinementArrayIndex === -1) {
                    refinementArray = [
                        ...refinementArray,
                        `${defaultFilterKey}${refineFilterKeyValueDelimiter}${defaultFilterValue}`,
                    ];
                } else {
                    const existingFilterPair =
                        refinementArray[commonFilterRefinementArrayIndex];
                    const [existingFilterKey, existingFilterValue] =
                        splitRefinementPairs(
                            existingFilterPair,
                            refineFilterKeyValueDelimiter,
                        ) || [];
                    if (existingFilterValue) {
                        const existingFilterValuesArray =
                            splitByCommaOutsideParentheses(existingFilterValue);
                        const currentFilterValuesArray =
                            splitByCommaOutsideParentheses(defaultFilterValue);
                        refinementArray[commonFilterRefinementArrayIndex] =
                            `${existingFilterKey}${refineFilterKeyValueDelimiter}${[...new Set([...existingFilterValuesArray, ...currentFilterValuesArray])].join(',')}`;
                    }
                }
            },
        );
    }

    // Check and add default refinements if refinement filter changed is not a default refinement
    const currentQueryParamContainingFilterPairs = refinementArray.filter(
        filterPair => filterPair.includes(queryParamKey),
    );

    let updatedFilterPair = '';

    // Filter key pair is present part of the url.
    // Key is present but current selected value may or may not be present.
    if (currentQueryParamContainingFilterPairs?.length > 0) {
        let existingFilterValues: string[] = [];
        currentQueryParamContainingFilterPairs.forEach(currentFilterPair => {
            const [_key, existingCurrentFilterValue] =
                splitRefinementPairs(
                    currentFilterPair,
                    refineFilterKeyValueDelimiter,
                ) ?? '';

            if (!existingCurrentFilterValue) {
                console.warn(
                    `Missing existing filter value for filter pair: ${currentFilterPair}`,
                );
                return;
            }

            const existingCurrentFilterValues = splitByCommaOutsideParentheses(
                existingCurrentFilterValue,
            );

            existingFilterValues = [
                ...existingFilterValues,
                ...existingCurrentFilterValues,
            ];
        });

        const isCurrentSelectedFilterValuePresent = existingFilterValues.some(
            value => value?.toLowerCase() === queryParamValue?.toLowerCase(),
        );

        // If filter value is enabled and current selected value is not present, add it to the existing values.
        if (isFilterEnabled && !isCurrentSelectedFilterValuePresent) {
            const updatedValues = [...existingFilterValues, queryParamValue];
            updatedFilterPair = `${queryParamKey}${refineFilterKeyValueDelimiter}${updatedValues.join(',')}`;
        } else if (isFilterEnabled && isCurrentSelectedFilterValuePresent) {
            // If filter value is enabled and current selected value is present, retain the same filter as updated filter except for show out of stock in LW.
            const updatedValues = existingFilterValues;
            updatedFilterPair = `${queryParamKey}${refineFilterKeyValueDelimiter}${updatedValues.join(',')}`;
        } else if (!isFilterEnabled && isCurrentSelectedFilterValuePresent) {
            // If filter value is disabled and current selected value is present, remove it from the existing values.
            const updatedValues = existingFilterValues.filter(
                value =>
                    value?.toLowerCase() !== queryParamValue?.toLowerCase(),
            );
            if (updatedValues.length > 0) {
                updatedFilterPair = `${queryParamKey}${refineFilterKeyValueDelimiter}${updatedValues.join(',')}`;
            }
        }

        // Removing the previous values as we will be adding the updated values.
        refinementArray = refinementArray.filter(
            filterPair => !filterPair?.includes(queryParamKey),
        );
    } else {
        // Filter key pair is not present in the url. Filter is enabled, so we can add the new filter pair.
        if (isFilterEnabled) {
            updatedFilterPair = `${queryParamKey}${refineFilterKeyValueDelimiter}${queryParamValue}`;
        }
    }

    // Finally adding the updated filter pair if exists.
    if (updatedFilterPair) {
        refinementArray.push(updatedFilterPair);
    }

    // If turning off delivery refinement and it's the only one, remove the param entirely
    if (
        !isFilterEnabled &&
        refinementArray?.length === 1 &&
        DEFAULT_REFINEMENT_FILTER_PAIRS.some(pair =>
            refinementArray.includes(pair),
        )
    ) {
        refinementArray = [];
    }

    if (
        refinementArray.length > 0 ||
        // Considering unchecking when delivery facet is selected by default to be unchecked.
        (refinementArray.length === 0 &&
            !isFilterEnabled &&
            queryParamKey === LW_DELIVERY_FACET_KEY)
    ) {
        queryParams.set(
            refinementKey,
            refinementArray.join(REFINE_QUERY_DELIMITER),
        );
    } else {
        queryParams.delete(refinementKey);
    }

    updateSearchParams(updateSearchParamsProvider, queryParams);
};

export function updateSearchParams(
    updateSearchParamsProvider: (queryParams: ReadonlyURLSearchParams) => void,
    searchParams: ReadonlyURLSearchParams | URLSearchParams,
) {
    const queryParams = new URLSearchParams(searchParams);

    // Use the history API to update the URL without reloading the page
    window.history.pushState(
        {},
        '',
        `${window.location.pathname}?${queryParams.toString()}`,
    );
    window.scrollTo(0, 0);
    updateSearchParamsProvider(queryParams as ReadonlyURLSearchParams);
}

export function updatePageParam(
    updateSearchParamsProvider: (queryParams: ReadonlyURLSearchParams) => void,
    searchParams: ReadonlyURLSearchParams,
    pageNumber: string,
) {
    const queryParams = new URLSearchParams(searchParams);
    queryParams.set('currentPage', pageNumber);
    updateSearchParams(updateSearchParamsProvider, queryParams);
}

export const getFilterType = (facetKey: string) => {
    let filterType;

    if (facetKey === LW_RATING_FACET_KEY || facetKey === GRS_RATING_FACET_KEY) {
        filterType = 'Customer Reviews';
    } else if (
        facetKey === LW_PRICE_FACET_KEY ||
        isGRSPriceFacetKey(facetKey)
    ) {
        filterType = 'Price';
    } else if (
        facetKey === LW_DELIVERY_FACET_KEY ||
        facetKey === GRS_DELIVERY_FACET_KEY ||
        facetKey === LW_TWO_DAY_FACET_KEY ||
        facetKey === GRS_TWO_DAY_FACET_KEY ||
        facetKey === LW_BUY_IN_WAREHOUSE_FACET_KEY ||
        facetKey === GRS_BUY_IN_WAREHOUSE_FACET_KEY ||
        facetKey === LW_SHOW_OUT_OF_STOCK_FACET_KEY ||
        facetKey === GRS_SHOW_OUT_OF_STOCK_FACET_KEY ||
        facetKey === LW_BOPIW_FACET_KEY ||
        facetKey === GRS_BOPIW_FACET_KEY ||
        facetKey === LW_BOPIM_FACET_KEY ||
        facetKey === GRS_BOPIM_FACET_KEY
    ) {
        filterType = 'Location';
    } else if (facetKey.includes(GRS_ATTRIBUTE_FACET_KEY_PREFIX)) {
        filterType = facetKey.split(GRS_ATTRIBUTE_FACET_KEY_PREFIX)[1];
    } else if (facetKey.includes(LW_ATTRIBUTE_FACET_KEY_SUFFIX)) {
        filterType = facetKey.split(LW_ATTRIBUTE_FACET_KEY_SUFFIX)[0];
    } else {
        filterType = facetKey
            ?.replace(LW_ATTRIBUTE_FACET_KEY_SUFFIX, '')
            .replace(GRS_ATTRIBUTE_FACET_KEY_PREFIX, '');
    }

    return filterType ? SHOP_BY_PREFIX + filterType.replace(/_/g, ' ') : '';
};

export const getSelectedFacets = (
    urlFilterPairs: string[] | null,
    normalizedFacets: FacetNormalized[],
    searchService: SearchServiceProps,
    locale: LocaleProps,
    grsFacetConfig: ContentStackEntryDataProps<GRSFacetConfigProperties>,
    facetConfig: ContentStackEntryDataProps,
): FacetNormalized[] => {
    let grsFacetsMap: GRSFacetConfig = {};
    let refineFilterKeyValueDelimiter:
        | typeof LW_REFINE_FILTER_KEY_VALUE_DELIMITER
        | typeof GRS_REFINE_FILTER_KEY_VALUE_DELIMITER =
        LW_REFINE_FILTER_KEY_VALUE_DELIMITER;
    if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
        refineFilterKeyValueDelimiter = GRS_REFINE_FILTER_KEY_VALUE_DELIMITER;
        if (grsFacetConfig) {
            grsFacetsMap = getGRSFacetsFromConfig(
                grsFacetConfig as ContentStackEntryDataProps<GRSFacetConfigProperties>,
            );
        }
    }
    const urlFlterPairsWithDefaults: string[] =
        urlFilterPairs?.length === 0
            ? DEFAULT_REFINEMENT_FILTER_PAIRS?.filter(defaultPair =>
                  defaultPair?.includes(refineFilterKeyValueDelimiter),
              )
            : urlFilterPairs || [];
    const selectedFacets: FacetNormalized[] = urlFlterPairsWithDefaults.map(
        filterPair => {
            const [filterKey, filterValue] = splitRefinementPairs(
                filterPair,
                refineFilterKeyValueDelimiter,
            );
            const filterValuesArray = splitByCommaOutsideParentheses(
                filterValue || '',
            );
            const facetKey =
                searchService === SEARCH_SERVICE_PROVIDERS.GRS
                    ? getFacetKeyFromQueryParamKey(
                          filterKey || '',
                          grsFacetsMap,
                      ) || filterKey
                    : filterKey;
            let currentNormalizedFacet = normalizedFacets.find(
                facet => facet?.facetKey === facetKey,
            );
            const updatedSelectedBuckets = filterValuesArray.map(
                filterValue => {
                    const matchedNormalizedBucket =
                        currentNormalizedFacet?.buckets.find(bucket => {
                            const bucketVal = getQueryParamValue(
                                currentNormalizedFacet as FacetNormalized,
                                bucket,
                                searchService,
                                facetConfig,
                            );
                            return bucketVal === filterValue;
                        });

                    if (matchedNormalizedBucket) {
                        return matchedNormalizedBucket;
                    } else {
                        const currentBucket = {
                            val: filterValue,
                        } as FacetBucketNormalized;
                        if (
                            facetKey === LW_RATING_FACET_KEY ||
                            facetKey === GRS_RATING_FACET_KEY ||
                            facetKey === LW_PRICE_FACET_KEY ||
                            isGRSPriceFacetKey(facetKey ?? '')
                        ) {
                            const [low, high] =
                                filterValue?.match(/\d+/g) || [];
                            if (
                                facetKey === LW_RATING_FACET_KEY ||
                                facetKey === GRS_RATING_FACET_KEY
                            ) {
                                if (low) {
                                    const ratingLow = parseInt(low);
                                    const ratingHigh = high
                                        ? parseInt(high) + 1
                                        : ratingLow + 1;
                                    currentBucket.val = ratingLow;
                                    currentBucket.low = ratingLow;
                                    currentBucket.high = ratingHigh;
                                }
                            }
                            if (
                                facetKey === LW_PRICE_FACET_KEY ||
                                isGRSPriceFacetKey(facetKey ?? '')
                            ) {
                                if (low) {
                                    currentBucket.low = parseInt(low);
                                }
                                if (high) {
                                    currentBucket.high = parseInt(high);
                                }
                                if (currentBucket.low && currentBucket.high) {
                                    currentBucket.val =
                                        getComposableTextByDevFieldKey({
                                            devFieldKey: PRICE_LABEL_KEY,
                                            composableWindow:
                                                facetConfig.composable_window as ComposableWindowArray,
                                            templateVariables: {
                                                priceMinimum: getFormattedPrice(
                                                    locale,
                                                    currentBucket.low,
                                                    true,
                                                ),
                                                priceMaximum: getFormattedPrice(
                                                    locale,
                                                    currentBucket.high,
                                                    true,
                                                ),
                                            },
                                        });
                                }
                            }
                        }
                        return currentBucket;
                    }
                },
            );
            if (currentNormalizedFacet) {
                currentNormalizedFacet = {
                    ...currentNormalizedFacet,
                    buckets: updatedSelectedBuckets,
                };
            } else {
                currentNormalizedFacet = {
                    facetKey: facetKey || '',
                    queryParamKey: filterKey || '',
                    buckets: updatedSelectedBuckets,
                } as FacetNormalized;
            }
            return currentNormalizedFacet;
        },
    );

    let selectedFacetsWithNegations: FacetNormalized[] =
        structuredClone(selectedFacets);

    const eligibleNegationFilters = NEGATION_REFINEMENT_FILTER_PAIRS.filter(
        negFilterPair => negFilterPair.includes(refineFilterKeyValueDelimiter),
    );

    eligibleNegationFilters.forEach(negatedFilterPair => {
        const [negatedFilterKey, negatedFilterValue] = splitRefinementPairs(
            negatedFilterPair,
            refineFilterKeyValueDelimiter,
        );
        const negatedFilterValueArray = splitByCommaOutsideParentheses(
            negatedFilterValue || '',
        );

        const matchedFacet = selectedFacets.find(
            facet => facet.facetKey === negatedFilterKey,
        );

        let selectedBucketsWithNegations: FacetBucketNormalized[] = matchedFacet
            ? structuredClone(matchedFacet?.buckets || [])
            : [];

        negatedFilterValueArray.forEach(negFilterValue => {
            if (
                selectedBucketsWithNegations.some(
                    selectedBucketWithNegation =>
                        selectedBucketWithNegation.val === negFilterValue,
                )
            ) {
                selectedBucketsWithNegations =
                    selectedBucketsWithNegations.filter(
                        bucket => bucket.val !== negFilterValue,
                    );
            } else {
                selectedBucketsWithNegations.push({
                    val: negFilterValue,
                } as FacetBucketNormalized);
            }
        });

        // Removing the existing facet.
        if (matchedFacet) {
            if (selectedBucketsWithNegations.length === 0) {
                selectedFacetsWithNegations = selectedFacets.filter(
                    facet => facet.facetKey !== negatedFilterKey,
                );
            } else {
                selectedFacetsWithNegations = selectedFacetsWithNegations.map(
                    facet => {
                        if (facet.facetKey === negatedFilterKey) {
                            return {
                                ...facet,
                                buckets: selectedBucketsWithNegations,
                            };
                        }
                        return facet;
                    },
                );
            }
        } else {
            const currentSelectedFacet = {
                facetKey: negatedFilterKey || '',
                queryParamKey: negatedFilterKey,
                buckets: selectedBucketsWithNegations,
            } as FacetNormalized;
            selectedFacetsWithNegations.push(currentSelectedFacet);
        }
    });

    return selectedFacetsWithNegations;
};

export const getReducedFilterValues = (
    refinementArray: string[],
    filterKey: string,
    refineFilterKeyValueDelimiter:
        | typeof LW_REFINE_FILTER_KEY_VALUE_DELIMITER
        | typeof GRS_REFINE_FILTER_KEY_VALUE_DELIMITER,
) => {
    const gatheredSimilarFilterPairs = refinementArray.filter(
        filterKeyValuePair =>
            filterKeyValuePair.startsWith(
                `${filterKey}${refineFilterKeyValueDelimiter}`,
            ),
    );
    const gatheredSameFilterValues = gatheredSimilarFilterPairs.map(
        filterKeyValuePair => {
            const [, filterValue] = splitRefinementPairs(
                filterKeyValuePair,
                refineFilterKeyValueDelimiter,
            );
            return filterValue;
        },
    );
    return gatheredSameFilterValues.join(',');
};

export const getReducedFilterKeyValuePairs = (
    searchParams: ReadonlyURLSearchParams,
    searchService: SearchServiceProps,
) => {
    const refinementKey =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_QUERY_PARAM
            : LW_REFINE_QUERY_PARAM;
    const refineFilterKeyValueDelimiter =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_FILTER_KEY_VALUE_DELIMITER
            : LW_REFINE_FILTER_KEY_VALUE_DELIMITER;
    const hasRefineString = searchParams.has(refinementKey);
    const refinementFilterPairsArray =
        fullyDecodeURI(searchParams.get(refinementKey))?.split(
            REFINE_QUERY_DELIMITER,
        ) || [];

    // To handle the use case where deliver refinement is unchecked.
    if (hasRefineString && refinementFilterPairsArray.length === 0) {
        return null;
    }

    const reducedFilterPairsArray: string[] = [];
    const reducedFilterKeys: string[] = [];
    refinementFilterPairsArray.forEach(filterKeyValuePair => {
        const [queryParamKey] = splitRefinementPairs(
            filterKeyValuePair,
            refineFilterKeyValueDelimiter,
        );

        if (queryParamKey && !reducedFilterKeys.includes(queryParamKey)) {
            reducedFilterKeys.push(queryParamKey);
            const reducedFilterValue = getReducedFilterValues(
                refinementFilterPairsArray,
                queryParamKey,
                refineFilterKeyValueDelimiter,
            );
            const reducedFilterPair = `${queryParamKey}${refineFilterKeyValueDelimiter}${reducedFilterValue}`;
            reducedFilterPairsArray.push(reducedFilterPair);
        }
    });
    return reducedFilterPairsArray;
};

export const getSelectedFacetsFromUrl = ({
    searchParams,
    searchResult,
    grsFacetConfig,
    locale,
    searchService,
    facetConfig,
}: {
    searchParams: ReadonlyURLSearchParams;
    searchResult: SearchResult;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    locale: LocaleProps;
    searchService: SearchServiceProps;
    facetConfig: ContentStackEntryDataProps;
}) => {
    const normalizedFacets =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? normalizeGRSFacets(
                  (searchResult as SearchResultGRS)?.searchResult?.facets,
                  grsFacetConfig as ContentStackEntryDataProps<GRSFacetConfigProperties>,
                  searchResult?.isAdTargetingExplicitlyDisabled,
                  locale,
              )
            : (searchResult as SearchResultLW).facets;

    if (!normalizedFacets) {
        return [];
    }

    const reducedFilterKeyValuePairs = getReducedFilterKeyValuePairs(
        searchParams,
        searchService,
    );

    const selectedFacets = getSelectedFacets(
        reducedFilterKeyValuePairs,
        normalizedFacets,
        searchService,
        locale,
        grsFacetConfig as ContentStackEntryDataProps<GRSFacetConfigProperties>,
        facetConfig,
    );

    return selectedFacets;
};

export const getSingleNonDefaultRefinementLabel = ({
    searchService,
    grsFacetConfig,
    refine,
    facetConfig,
}: {
    searchService: SearchServiceProps;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    refine: string;
    facetConfig?: ContentStackEntryDataProps;
}) => {
    let existingNonDefaultRefinementLabel = '';
    let grsFacetsMap: GRSFacetConfig = {};
    let refineFilterKeyValueDelimiter:
        | typeof LW_REFINE_FILTER_KEY_VALUE_DELIMITER
        | typeof GRS_REFINE_FILTER_KEY_VALUE_DELIMITER =
        LW_REFINE_FILTER_KEY_VALUE_DELIMITER;
    if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
        refineFilterKeyValueDelimiter = GRS_REFINE_FILTER_KEY_VALUE_DELIMITER;
        if (grsFacetConfig) {
            grsFacetsMap = getGRSFacetsFromConfig(
                grsFacetConfig as ContentStackEntryDataProps<GRSFacetConfigProperties>,
            );
        }
    }
    const existingRefinementPairs =
        fullyDecodeURI(refine)?.split(REFINE_QUERY_DELIMITER) || [];
    // Calculate non-default refinement pairs present in the url.
    const existingNonDefaultRefinementPairs = existingRefinementPairs.filter(
        existingRefinementPair =>
            isNonDefaultRefinementPair(
                existingRefinementPair,
                refineFilterKeyValueDelimiter,
                searchService,
                grsFacetsMap,
            ),
    );

    // Update the existingNonDefaultRefinementLabel based on the non-default refinement pairs
    if (existingNonDefaultRefinementPairs.length === 1) {
        const [
            existingNonDefaultRefinementKey,
            existingNonDefaultRefinementValue,
        ] = splitRefinementPairs(
            existingNonDefaultRefinementPairs[0],
            refineFilterKeyValueDelimiter,
        );
        const existingRefinementValues = splitByCommaOutsideParentheses(
            existingNonDefaultRefinementValue!,
        );
        // Checking considering multiple values can be present in the refinement value
        if (existingRefinementValues.length === 1) {
            const existingRefinementKey =
                searchService === SEARCH_SERVICE_PROVIDERS.GRS
                    ? (grsFacetsMap[existingNonDefaultRefinementKey || '']
                          ?.queryParamKey ?? existingNonDefaultRefinementKey)
                    : existingNonDefaultRefinementKey;

            if (
                existingRefinementKey === LW_RATING_FACET_KEY ||
                existingRefinementKey === GRS_RATING_FACET_KEY
            ) {
                const [ratingLow, _ratingHigh] =
                    existingNonDefaultRefinementValue?.match(/\d+/g) || [];
                if (ratingLow) {
                    const composableRatingTagText =
                        facetConfig &&
                        getComposableTextByDevFieldKey({
                            devFieldKey: RATING_TAG_LABEL_KEY,
                            composableWindow:
                                facetConfig.composable_window as ComposableWindowArray,
                            templateVariables: {
                                ratingMinimum: ratingLow?.toString(),
                            },
                        });
                    existingNonDefaultRefinementLabel =
                        composableRatingTagText || `${ratingLow} Stars & Up`;
                }
            } else {
                existingNonDefaultRefinementLabel =
                    existingNonDefaultRefinementValue!;
            }
        }
    }
    return existingNonDefaultRefinementLabel;
};

export const getSingleNonDefaultRefinementsCount = ({
    searchService,
    grsFacetConfig,
    refine,
}: {
    searchService: SearchServiceProps;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    refine: string;
}) => {
    let existingNonDefaultRefinementsCount = 0;
    let grsFacetsMap: GRSFacetConfig = {};
    let refineFilterKeyValueDelimiter:
        | typeof LW_REFINE_FILTER_KEY_VALUE_DELIMITER
        | typeof GRS_REFINE_FILTER_KEY_VALUE_DELIMITER =
        LW_REFINE_FILTER_KEY_VALUE_DELIMITER;
    if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
        refineFilterKeyValueDelimiter = GRS_REFINE_FILTER_KEY_VALUE_DELIMITER;
        if (grsFacetConfig) {
            grsFacetsMap = getGRSFacetsFromConfig(
                grsFacetConfig as ContentStackEntryDataProps<GRSFacetConfigProperties>,
            );
        }
    }
    const existingRefinementPairs =
        fullyDecodeURI(refine)?.split(REFINE_QUERY_DELIMITER) || [];
    // Calculate non-default refinement pairs present in the url.
    const existingNonDefaultRefinementPairs = existingRefinementPairs.filter(
        existingRefinementPair =>
            isNonDefaultRefinementPair(
                existingRefinementPair,
                refineFilterKeyValueDelimiter,
                searchService,
                grsFacetsMap,
            ),
    );

    // Update the existingNonDefaultRefinementLabel based on the non-default refinement pairs
    existingNonDefaultRefinementPairs.forEach(
        existingNonDefaultRefinementPair => {
            const [
                _existingNonDefaultRefinementKey,
                existingNonDefaultRefinementValue,
            ] = splitRefinementPairs(
                existingNonDefaultRefinementPair,
                refineFilterKeyValueDelimiter,
            );
            const existingRefinementValues = splitByCommaOutsideParentheses(
                existingNonDefaultRefinementValue!,
            );
            // Checking considering multiple values can be present in the refinement value
            existingNonDefaultRefinementsCount +=
                existingRefinementValues.length;
        },
    );
    return existingNonDefaultRefinementsCount;
};

export const getFacetTagLabel = ({
    facetConfig,
    grsFacetConfig: _grsFacetConfig,
    facet,
    bucket,
    locale,
}: {
    facetConfig: ContentStackEntryDataProps;
    grsFacetConfig: ContentStackEntryDataProps<GRSFacetConfigProperties>;
    facet: FacetNormalized;
    bucket: FacetBucketNormalized;
    locale: LocaleProps;
}) => {
    const facetKey = facet.facetKey;
    if (
        (facetKey === LW_DELIVERY_FACET_KEY ||
            facetKey === GRS_DELIVERY_FACET_KEY) &&
        bucket.val === DELIVERY_FACET_VALUE
    ) {
        return getKeyValue(DELIVERY_TEXT_KEY, facetConfig);
    } else if (
        (facetKey === LW_TWO_DAY_FACET_KEY ||
            facetKey === GRS_TWO_DAY_FACET_KEY) &&
        bucket.val === TWO_DAY_FACET_VALUE
    ) {
        return getKeyValue(TWO_DAY_DELIVERY_TEXT_KEY, facetConfig);
    } else if (
        (facetKey === LW_BUY_IN_WAREHOUSE_FACET_KEY &&
            bucket.val === BUY_IN_WAREHOUSE_FACET_VALUE) ||
        facetKey === GRS_BUY_IN_WAREHOUSE_FACET_KEY
    ) {
        return getKeyValue(BUY_IN_WAREHOUSE_TEXT_KEY, facetConfig);
    } else if (
        facetKey === LW_BOPIM_FACET_KEY ||
        facetKey === GRS_BOPIM_FACET_KEY
    ) {
        return getKeyValue(BOPIM_PICK_UP_TEXT_KEY, facetConfig);
    } else if (
        facetKey === LW_SHOW_OUT_OF_STOCK_FACET_KEY ||
        facetKey === GRS_SHOW_OUT_OF_STOCK_FACET_KEY
    ) {
        return getKeyValue(OUT_OF_STOCK_TEXT_KEY, facetConfig);
    } else if (
        facetKey === LW_BOPIW_FACET_KEY ||
        facetKey === GRS_BOPIW_FACET_KEY
    ) {
        return getKeyValue(PICK_UP_TEXT_KEY, facetConfig);
    } else if (
        facetKey === LW_RATING_FACET_KEY ||
        facetKey === GRS_RATING_FACET_KEY
    ) {
        const currentValue = parseInt(
            bucket.low?.toString() || bucket.val?.toString(),
        );
        const composableRatingTagText =
            facetConfig &&
            getComposableTextByDevFieldKey({
                devFieldKey: RATING_TAG_LABEL_KEY,
                composableWindow:
                    facetConfig.composable_window as ComposableWindowArray,
                templateVariables: {
                    ratingMinimum: currentValue.toString(),
                },
            });
        return composableRatingTagText ?? `${currentValue} Stars & Up`;
    } else if (
        facetKey === LW_PRICE_FACET_KEY ||
        isGRSPriceFacetKey(facetKey)
    ) {
        const composablePriceTagText =
            facetConfig &&
            getComposableTextByDevFieldKey({
                devFieldKey: PRICE_LABEL_KEY,
                composableWindow:
                    facetConfig.composable_window as ComposableWindowArray,
                templateVariables: {
                    priceMinimum: getFormattedPrice(locale, bucket.low, true),
                    priceMaximum: getFormattedPrice(locale, bucket.high, true),
                },
            });
        return (
            composablePriceTagText ??
            `${getFormattedPrice(locale, bucket.low, true)} to ${getFormattedPrice(locale, bucket.high, true)}`
        );
    } else {
        return bucket.val.toString();
    }
};

export function splitRefinementPairs(
    refinementPair: string | undefined,
    refineFilterKeyValueDelimiter:
        | typeof GRS_REFINE_FILTER_KEY_VALUE_DELIMITER
        | typeof LW_REFINE_FILTER_KEY_VALUE_DELIMITER,
): [string | undefined, string | undefined] {
    if (!refinementPair) {
        return [undefined, undefined];
    }

    const delimiterIndex = refinementPair.indexOf(
        refineFilterKeyValueDelimiter,
    );

    if (delimiterIndex === -1) {
        return [undefined, undefined];
    }

    // Dashes are valid in refinement values, so make sure we only split on the first occurrence of the delimiter
    const key = refinementPair.substring(0, delimiterIndex);
    const value = refinementPair.substring(
        delimiterIndex + refineFilterKeyValueDelimiter.length,
    );

    return [key, value];
}
